import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  DollarSign, Plus, Send, CheckCircle, FileText, 
  AlertCircle, Calendar, Loader2, X 
} from 'lucide-react';
import { showToast } from '../../utils/toast';
import { formatDateEST } from '../../utils/timezone';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminBillingSection({ clientId, onUpdate }) {
  const [billingSummary, setBillingSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(null);
  const [paying, setPaying] = useState(null);

  // Create invoice form
  const [newInvoice, setNewInvoice] = useState({
    amount: '',
    description: '',
    due_date: ''
  });

  useEffect(() => {
    if (clientId) {
      fetchBillingData();
    }
  }, [clientId]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      
      // Fetch billing summary
      const summaryResponse = await fetch(`${API}/api/admin/billing/summary/${clientId}`, {
        credentials: 'include'
      });
      
      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        setBillingSummary(summaryData);
      }
      
      // Fetch client data for invoices
      const clientResponse = await fetch(`${API}/api/admin/clients/${clientId}`, {
        credentials: 'include'
      });
      
      if (clientResponse.ok) {
        const clientData = await clientResponse.json();
        setInvoices(clientData.invoices || []);
      }
    } catch (error) {
      console.error('Error fetching billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!newInvoice.amount || !newInvoice.description || !newInvoice.due_date) {
      showToast({
        title: 'Missing information',
        message: 'Please fill in all fields',
        type: 'error'
      });
      return;
    }

    setCreating(true);

    try {
      const response = await fetch(`${API}/api/admin/invoices/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: clientId,
          amount: parseFloat(newInvoice.amount),
          description: newInvoice.description,
          due_date: newInvoice.due_date,
          line_items: []
        })
      });

      if (response.ok) {
        showToast({
          title: 'Invoice created',
          message: 'Invoice has been created successfully',
          type: 'success'
        });
        setShowCreateModal(false);
        setNewInvoice({ amount: '', description: '', due_date: '' });
        await fetchBillingData();
        if (onUpdate) onUpdate();
      } else {
        throw new Error('Failed to create invoice');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      showToast({
        title: 'Error',
        message: 'Failed to create invoice',
        type: 'error'
      });
    } finally {
      setCreating(false);
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    setSending(invoiceId);

    try {
      const response = await fetch(`${API}/api/admin/invoices/${invoiceId}/send`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        showToast({
          title: 'Invoice sent',
          message: 'Invoice has been sent to client',
          type: 'success'
        });
        await fetchBillingData();
        if (onUpdate) onUpdate();
      } else {
        throw new Error('Failed to send invoice');
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      showToast({
        title: 'Error',
        message: 'Failed to send invoice',
        type: 'error'
      });
    } finally {
      setSending(null);
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    if (!window.confirm('Mark this invoice as paid? This will trigger contract activation if applicable.')) {
      return;
    }

    setPaying(invoiceId);

    try {
      const response = await fetch(`${API}/api/admin/invoices/${invoiceId}/mark-paid`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        showToast({
          title: 'Invoice marked as paid',
          message: 'Invoice has been marked as paid',
          type: 'success'
        });
        await fetchBillingData();
        if (onUpdate) onUpdate();
      } else {
        throw new Error('Failed to mark as paid');
      }
    } catch (error) {
      console.error('Error marking as paid:', error);
      showToast({
        title: 'Error',
        message: 'Failed to mark invoice as paid',
        type: 'error'
      });
    } finally {
      setPaying(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return formatDateEST(dateString);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700 border-gray-300',
      sent: 'bg-blue-100 text-blue-700 border-blue-300',
      paid: 'bg-green-100 text-green-700 border-green-300',
      overdue: 'bg-red-100 text-red-700 border-red-300',
      failed: 'bg-red-100 text-red-700 border-red-300'
    };

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.sent}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Billing & Invoices
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Billing Summary */}
          {billingSummary && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-sm text-gray-900">Billing Summary</h4>
                {billingSummary.overdue_amount > 0 && (
                  <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full border border-red-300 font-medium">
                    Overdue
                  </span>
                )}
                {billingSummary.outstanding_balance === 0 && billingSummary.invoice_count > 0 && (
                  <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full border border-green-300 font-medium">
                    All caught up
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-gray-600 text-xs mb-1">Total Invoiced</p>
                  <p className="font-semibold text-gray-900">{formatCurrency(billingSummary.total_invoiced)}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs mb-1">Total Paid</p>
                  <p className="font-semibold text-green-600">{formatCurrency(billingSummary.total_paid)}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs mb-1">Outstanding</p>
                  <p className="font-semibold text-yellow-600">{formatCurrency(billingSummary.outstanding_balance)}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs mb-1">Overdue</p>
                  <p className="font-semibold text-red-600">{formatCurrency(billingSummary.overdue_amount)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Invoice List */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-sm text-gray-900">Invoices ({invoices.length})</h4>
              <Button size="sm" onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Create Invoice
              </Button>
            </div>

            {invoices.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No invoices yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {invoices.map((invoice) => (
                  <div
                    key={invoice.invoice_id}
                    className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-sm text-gray-900">{invoice.invoice_number}</p>
                          {getStatusBadge(invoice.status)}
                        </div>
                        <p className="text-xs text-gray-600 truncate">{invoice.description}</p>
                      </div>
                      <p className="font-semibold text-gray-900 ml-2">{formatCurrency(invoice.amount)}</p>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Due: {formatDate(invoice.due_date)}</span>
                      <div className="flex gap-2">
                        {['draft', 'sent'].includes(invoice.status) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSendInvoice(invoice.invoice_id)}
                            disabled={sending === invoice.invoice_id}
                            className="h-7 text-xs"
                          >
                            {sending === invoice.invoice_id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <>
                                <Send className="h-3 w-3 mr-1" />
                                Send
                              </>
                            )}
                          </Button>
                        )}
                        {['sent', 'overdue'].includes(invoice.status) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleMarkPaid(invoice.invoice_id)}
                            disabled={paying === invoice.invoice_id}
                            className="h-7 text-xs text-green-600 hover:text-green-700"
                          >
                            {paying === invoice.invoice_id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Mark Paid
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create Invoice</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  placeholder="950.00"
                  value={newInvoice.amount}
                  onChange={(e) => setNewInvoice({ ...newInvoice, amount: e.target.value })}
                />
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea
                  placeholder="Monthly subscription, services rendered, etc."
                  value={newInvoice.description}
                  onChange={(e) => setNewInvoice({ ...newInvoice, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div>
                <Label>Due Date *</Label>
                <Input
                  type="date"
                  value={newInvoice.due_date}
                  onChange={(e) => setNewInvoice({ ...newInvoice, due_date: e.target.value })}
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1"
                  disabled={creating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateInvoice}
                  className="flex-1"
                  disabled={creating}
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Create
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
